/* eslint-disable no-unused-vars */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  PlanTypeV4,
  AudienceList,
  AudienceListVisibility,
} from "@markit/common.types";
import { useDispatch, useSelector } from "react-redux";
import {
  createInitialFavoritesList,
  getAccountState,
} from "../../../../redux/slices/accountSlice";
import {
  checkHitAudienceListsLimit,
  FAVORITES_LIST_NAME,
  hasSubscription,
  sortAudienceListsDisplay,
} from "@markit/common.utils";
import EmptyStateButton from "../../../../components/Buttons/EmptyStateButton";
import { Icon } from "@iconify/react";
import { Colors } from "../../../../utils/colors";
import { HorizontalDivider } from "../../../../components/Dividers/HorizontalDivider";
import RectangleButtonCreatorPanel from "../../../../components/Buttons/RectangleButtonCreatorPanel";
import MarkitPlusActionButton from "../../../../components/Subscription/MarkitPlusActionButton";
import CreatorModePanelWrapper from "../../../../components/CreatorDashboard/CreatorModePanelWrapper";
import SearchBoxContainer from "../../../../components/Containers/SearchBoxContainer";
import FlatList from "flatlist-react/lib";
import AudienceListItem, {
  AUDIENCE_LIST_FLEX_SECTIONS,
} from "../../../../components/DisplayItem/AudienceListItem/AudienceListItem";
import filter from "lodash.filter";
import { EmptyStateFlatlist } from "../../../../components/EmptyStates/EmptyStateFlatlist";
import StandardListContainer from "../../../../components/Containers/StandardListContainer";
import { DropdownMenuItem } from "../../../../components/DropdownMenu";
import AudienceListDetailsModal from "../../../../components/CreatorDashboard/CreatorProfile/AudienceLists/AudienceListDetailsModal";
import { notificationActions } from "../../../../redux/slices/notificationSlice";
import DropdownFilter from "../../../../components/DropdownsAndTabs/DropdownFilter";
import EmptyStateButtonSubscription from "../../../../components/Buttons/EmptyState/EmptyStateButtonSubscription";

// eslint-disable-next-line no-unused-vars
export enum ListFilterState {
  ANY = "Any Visibility",
  PRIVATE = "Private",
  PUBLIC = "Public",
}

const ListsPanel = () => {
  const { audienceListDetails, appInitialized, accountData } =
    useSelector(getAccountState).account;
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [listPopupVisible, setListPopupVisible] = useState(false);
  const [filterState, setFilterState] = useState<ListFilterState>(
    ListFilterState.ANY
  );
  const [existingList, setExistingList] = useState<AudienceList>();

  const styles = {
    container: {
      backgroundColor: Colors.WHITE,
      paddingInline: 30,
      paddingTop: 30,
      height: "100%",
    },
  };

  useEffect(() => {
    // Create the favorites list if it doesn't exist when you view the lists tab
    const hasFavoritesList = audienceListDetails.some(
      (listDetail) => listDetail.list.name === FAVORITES_LIST_NAME
    );
    if (appInitialized && !hasFavoritesList) {
      dispatch(createInitialFavoritesList(accountData));
    }
  }, [accountData, appInitialized, audienceListDetails, dispatch]);

  const audienceLists = useMemo(
    () => audienceListDetails.map((listDetail) => listDetail.list),
    [audienceListDetails]
  );

  const createdAudienceLists = useMemo(() => {
    return audienceLists.filter((list) => list.name !== FAVORITES_LIST_NAME);
  }, [audienceLists]);

  const contains = useCallback((list: AudienceList, query: string) => {
    return list.name.toLowerCase().includes(query);
  }, []);

  const handleSearch = (text: string) => {
    setSearchTerm(text.toLowerCase());
  };

  const audienceListsToShow = useMemo(() => {
    if (!appInitialized) {
      return [];
    }
    let userAudienceLists: AudienceList[] = sortAudienceListsDisplay(
      filterState === ListFilterState.ANY
        ? audienceLists
        : audienceLists.filter(
            (list) =>
              list.visibility ===
              (filterState === ListFilterState.PRIVATE
                ? AudienceListVisibility.PRIVATE
                : AudienceListVisibility.PUBLIC)
          )
    );
    if (searchTerm !== "") {
      userAudienceLists = filter(userAudienceLists, (list: AudienceList) => {
        return contains(list, searchTerm);
      });
    }
    return userAudienceLists;
  }, [appInitialized, audienceLists, contains, searchTerm, filterState]);

  const addNewList = useCallback(() => {
    if (checkHitAudienceListsLimit(createdAudienceLists, accountData)) {
      dispatch(
        notificationActions.setMarkitPlusModal(PlanTypeV4.ESSENTIAL.name)
      );
      return;
    }
    setListPopupVisible(true);
  }, [accountData, createdAudienceLists, dispatch]);

  const editListOnPress = useCallback((item: AudienceList) => {
    setExistingList(item);
    setListPopupVisible(true);
  }, []);

  const dropdownItems: DropdownMenuItem[] = useMemo(
    () => [
      {
        title: ListFilterState.ANY,
        icon: filterState === ListFilterState.ANY ? "ion:checkmark" : undefined,
        key: "all_visibility",
        onPress: () => setFilterState(ListFilterState.ANY),
      },
      {
        title: ListFilterState.PRIVATE,
        icon:
          filterState === ListFilterState.PRIVATE ? "ion:checkmark" : undefined,
        key: "private_visibility",
        onPress: () => setFilterState(ListFilterState.PRIVATE),
      },
      {
        title: ListFilterState.PUBLIC,
        icon:
          filterState === ListFilterState.PUBLIC ? "ion:checkmark" : undefined,
        key: "public_visibility",
        onPress: () => setFilterState(ListFilterState.PUBLIC),
      },
    ],
    [filterState]
  );

  const renderEmptyView = useMemo(() => {
    return (
      <EmptyStateFlatlist
        searchTerm={searchTerm}
        isLoading={false}
        containerStyles={{ paddingTop: 150 }}
        nonSearchEmptyView={<></>}
      />
    );
  }, [searchTerm]);

  return (
    <CreatorModePanelWrapper
      title="Lists"
      headerRight={
        <div className="AlignedRow" style={{ gap: 14 }}>
          <MarkitPlusActionButton />
          <RectangleButtonCreatorPanel
            title="New List"
            iconName="ion:create"
            onPress={addNewList}
          />
        </div>
      }
    >
      <div style={styles.container}>
        <StandardListContainer
          searchComp={
            <div className="AlignedRow" style={{ gap: 14 }}>
              <SearchBoxContainer
                placeholder={"Search"}
                onChange={(e) => handleSearch(e.target.value)}
                containerStyles={{ marginTop: 0, width: 394 }}
              />
              <DropdownFilter items={dropdownItems} filterState={filterState} />
            </div>
          }
          gridTable={{
            flexSections: AUDIENCE_LIST_FLEX_SECTIONS,
            sectionTitles: ["Name", "Visibility", "People", "Description"],
          }}
          isLoading={!appInitialized}
          listComp={
            <div className="ColumnNormal">
              <FlatList
                list={audienceListsToShow}
                renderItem={(item: AudienceList) => (
                  <div key={item.id}>
                    <AudienceListItem
                      item={item}
                      editListOnPress={() => editListOnPress(item)}
                      hideDivider={
                        item.id ===
                        audienceListsToShow[audienceListsToShow.length - 1]?.id
                      }
                    />
                  </div>
                )}
                renderWhenEmpty={() => renderEmptyView}
              />
              {!hasSubscription(accountData) ? (
                <EmptyStateButtonSubscription
                  planType={PlanTypeV4.ESSENTIAL}
                  iconName={"ion:list"}
                  btnText="Get Essential Plan"
                  containerStyles={{ paddingBlock: 30 }}
                />
              ) : null}
            </div>
          }
        />
        {listPopupVisible ? (
          <AudienceListDetailsModal
            isVisible={listPopupVisible}
            setIsVisible={setListPopupVisible}
            existingList={existingList}
          />
        ) : null}
      </div>
    </CreatorModePanelWrapper>
  );
};

export default ListsPanel;
